import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { User } from '@gravity-ui/uikit';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigateReplace } from 'hooks';
import { privateApi, useInfoUserQuery } from 'store/api';
import { useAppDispatch } from 'store/hooks/use-app';
import { logout, setUser } from 'store/slices/user-slice';
import type { AppState } from 'store/store';
import { routes } from 'routes';

import { Container, Divider } from '../ui-kit';

import { Navigation } from './header-navigation';

import { NavLink } from 'react-router-dom';

import { useMediaQuery } from 'react-responsive';

import styles from './styles.module.scss';
import { NavigationMobile } from './header-navigation/header-navigation-mobile';

const ADMIN_MODE_STORAGE_KEY = 'adminMode';

export const Header = () => {
	const navigate = useNavigateReplace();
	const dispatch = useAppDispatch();
	const token = useSelector((state: AppState) => state.userState.token);
	const user = useSelector((state: AppState) => state.userState.user);

	const { data: response, error, isLoading, isError, isSuccess } = useInfoUserQuery(token ? null : skipToken);

	const isAdaptive = useMediaQuery({ query: '(max-width: 877px)' });

	const isMobile = useMediaQuery({ query: '(max-width: 515px)' });

	const iconSize = isAdaptive ? 'l' : 'm';

	// Reset RTK Query cache whenever auth token changes (login/logout)
	useEffect(() => {
		// token is string | null; run on any change to avoid stale caches
		dispatch(privateApi.util.resetApiState());
	}, [token, dispatch]);

	useEffect(() => {
		if (isSuccess && response) {
			dispatch(setUser(response));
		}
	}, [isSuccess, response, dispatch]);

	const data = useMemo(() => {
		return response;
	}, [response]);

	const role = user?.role;

	const [isAdminMode, setIsAdminMode] = useState(() => {
		return localStorage.getItem(ADMIN_MODE_STORAGE_KEY) === 'true';
	});

	useEffect(() => {
		localStorage.setItem(ADMIN_MODE_STORAGE_KEY, String(isAdminMode));
	}, [isAdminMode]);

	useEffect(() => {
		if (!role) return;

		if (role !== 'ADMIN') {
			setIsAdminMode(false);
			localStorage.setItem('adminMode', 'false');
		}
	}, [role]);

	useEffect(() => {
		if (isError) {
			toast.error((error as any).data.message, {
				position: 'top-right',
			});
			dispatch(logout());

			navigate(routes.login.path);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	return (
		<>
			{isMobile ? (
				<NavigationMobile role={role} isAdminMode={isAdminMode} setIsAdminMode={setIsAdminMode} />
			) : (
				<header className={styles.header}>
					<Container>
						<div className={styles.header__wrap}>
							{isAdaptive ? (
								user?.imageUrl ? (
									<NavLink to="profile">
										{({ isActive }) => (
											<User
												avatar={{
													imgUrl: `${user?.firstName} ${user?.lastName}`,
													fallbackImgUrl: user?.imageUrl,
												}}
												size={iconSize}
												className={styles.header__user_icon}
											/>
										)}
									</NavLink>
								) : (
									<NavLink to="profile">
										{({ isActive }) => (
											<User
												avatar={{
													text: `${user?.firstName} ${user?.lastName}`,
													theme: 'brand',
													backgroundColor: '#ffbe5c',
												}}
												size={iconSize}
												className={styles.header__user_icon}
											/>
										)}
									</NavLink>
								)
							) : user?.imageUrl ? (
								<User
									avatar={{
										imgUrl: `${user?.firstName} ${user?.lastName}`,
										fallbackImgUrl: user?.imageUrl,
									}}
									name={`${user?.firstName} ${user?.lastName}${', добро пожаловать!'}`}
									description={user?.email}
									size="m"
									className={styles.header__user}
								/>
							) : (
								<User
									avatar={{
										text: `${user?.firstName} ${user?.lastName}`,
										theme: 'brand',
										backgroundColor: '#ffbe5c',
									}}
									name={`${user?.firstName} ${user?.lastName}${', добро пожаловать!'}`}
									description={user?.email}
									size="m"
									className={styles.header__user}
								/>
							)}

							<Divider />
							<Navigation
								role={role}
								isAdminMode={isAdminMode}
								setIsAdminMode={setIsAdminMode}
								isAdaptive={isAdaptive}
							/>
						</div>
					</Container>
				</header>
			)}
		</>
	);
};
